/**
 * CASE.SHE — a model filter belongs to its device.
 *
 * Loaded by blocks/filters.liquid only when the block's «CASE.SHE: лише моделі
 * обраного пристрою» setting is on (the collection template). When the
 * customer unticks a device, the models chosen for that device are unticked
 * too, before Horizon reads the form (`FacetsFormComponent.createURLParameters`);
 * the device filter's own «Очистити» unticks every model. Without this, a
 * model outlives its device: «iPhone» off with «Модель iPhone: 12» still on
 * keeps the page on iPhones, and every other device shows zero results and
 * stays disabled until the customer finds and clears the model.
 *
 * A model of a device that is still ticked is kept, and a model that arrives
 * in the URL without a device choice is left alone: only the customer's own
 * device change triggers this.
 *
 * Which device a model parameter belongs to comes from docs/FILTER_TAXONOMY.md
 * (§2 device values, §3 model metafields; a model metafield is never set on a
 * product of another device family). If a device value is ever renamed, its
 * models are simply cleared on every device change — a filter is never hidden.
 * The four model parameters are the ones snippets/case-she-filter-gate.liquid
 * and snippets/case-she-filter-skip.liquid recognise; keep the lists identical.
 */
const DEVICE_PARAM = 'filter.p.m.custom.device_type';
const MODEL_DEVICE = new Map([
  ['filter.v.m.custom.iphone_model', 'iPhone'],
  ['filter.v.m.custom.macbook_model', 'MacBook'],
  ['filter.v.m.custom.ipad_model', 'iPad'],
  ['filter.v.m.custom.airpods_model', 'AirPods'],
]);

/**
 * Untick every model whose device is not ticked in `form`.
 * @param {HTMLFormElement | null | undefined} form
 * @param {{ devicesCleared?: boolean }} [options] - the device filter is being cleared as a whole
 */
function clearOrphanModels(form, { devicesCleared = false } = {}) {
  if (!form) return;
  const inputs = [...form.elements].filter((element) => element instanceof HTMLInputElement);
  const devices = new Set(
    devicesCleared ? [] : inputs.filter((input) => input.name === DEVICE_PARAM && input.checked).map((input) => input.value)
  );
  for (const input of inputs) {
    const device = MODEL_DEVICE.get(input.name);
    if (device !== undefined && input.checked && !devices.has(device)) input.checked = false;
  }
}

/**
 * The device filter's own «Очистити» button (Horizon's `clearFilter`).
 * @param {EventTarget | null} target
 */
function deviceClearButton(target) {
  const button = target instanceof Element ? target.closest('.clear-filter') : null;
  const facet = button?.closest('facet-inputs-component');
  return facet?.querySelector(`input[name="${DEVICE_PARAM}"]`) ? button : null;
}

// Horizon delegates its `on:change` / `on:click` attributes from `document` in
// the capture phase (assets/component.js), and those handlers read the form at
// once. Capture listeners on `window` run before any on `document`.
window.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.name === DEVICE_PARAM && !input.checked) clearOrphanModels(input.form);
  },
  true
);

window.addEventListener(
  'click',
  (event) => {
    const button = deviceClearButton(event.target);
    if (button) clearOrphanModels(button.closest('form'), { devicesCleared: true });
  },
  true
);

window.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const button = deviceClearButton(event.target);
    if (button) clearOrphanModels(button.closest('form'), { devicesCleared: true });
  },
  true
);
